import { useAntdTable, useRequest } from 'ahooks';
import { Button, Table, TableColumnsType, Progress, message, Typography } from 'antd';
import dayjs from 'dayjs';
import { head, last } from 'lodash-es';
import { ReactNode, useMemo, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { ExportLog, ExportLogStatus, getExportList, retryExport } from 'services/api/admin';
import download from 'utils/download';
import { useExportCount } from './hooks/useExportCenter';
import { formatLoggerData, logger } from 'services/logger';

const ExportCenterContent = () => {
  const { t } = useTranslate();
  const downloadCount = useExportCount();
  const { tableProps, data, mutate } = useAntdTable(
    async ({ pageSize, current: page }) => {
      try {
        const {
          data: { data, count },
        } = await getExportList({ page, pageSize });
        return {
          total: count,
          list: data,
        };
      } catch (error) {
        return {
          total: 0,
          list: [],
        };
      }
    },
    {
      refreshDeps: [
        downloadCount?.failCount,
        downloadCount?.inProgressCount,
        downloadCount?.successCount,
      ],
    },
  );
  const retryIdRef = useRef(-1);
  const { run: retry, loading } = useRequest(
    async (id: number) => {
      try {
        retryIdRef.current = id;
        await retryExport(id);
        message.info(t('export_modal_downloading'));
        const list =
          data?.list.map((x) => {
            if (x.id === id) {
              return {
                ...x,
                status: ExportLogStatus.inProgress,
              };
            }
            return x;
          }) ?? [];

        mutate({
          total: data?.total ?? 0,
          list,
        });
      } catch (error) {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      }
    },
    { manual: true },
  );
  const columns: TableColumnsType<ExportLog> = useMemo(() => {
    return [
      {
        dataIndex: 'id',
        title: t('export_modal_column_No'),
        fixed: true,
        render(value, record, index) {
          return index + 1;
        },
      },
      {
        dataIndex: 'fileName',
        title: t('export_modal_column_file_name'),
        fixed: true,
        render(value: any) {
          return (
            <Typography.Text
              ellipsis={{ tooltip: decodeURIComponent(value || '') }}
              className="w-[260px]"
            >
              {decodeURIComponent(value || '-')}
            </Typography.Text>
          );
        },
      },

      {
        dataIndex: 'createTimeStamp',
        title: t('export_modal_column_create_time'),
        render(_, record) {
          return dayjs(record.createTimeStamp).format('YYYY-MM-DD HH:mm:ss');
        },
      },
      {
        dataIndex: 'status',
        title: t('export_modal_column_status'),
        render(_, record) {
          const { status, errorMsg } = record;
          const transKey = `errorMsg.${errorMsg}`;
          const labelMap: Record<ExportLogStatus, ReactNode> = {
            [ExportLogStatus.inProgress]: <Progress percent={50} size="small" />,
            [ExportLogStatus.succeed]: <span>{t('export_modal_row_done')}</span>,
            [ExportLogStatus.failed]: (
              <div className="w-32 text-error text-wrap">
                {t(transKey, { defaultValue: errorMsg })}
              </div>
            ),
          };
          return <div className="w-32">{labelMap[status] || '-'}</div>;
        },
      },
      {
        dataIndex: '-',
        title: t('操作'),
        render(_, record) {
          const { status, id, url } = record;
          const filename = decodeURIComponent(
            head(last(url.split('/'))?.split('?')) ??
              `download_${dayjs('YYYY-MM-DD HH:mm:ss')}.xlsx`,
          );
          const labelMap: Record<ExportLogStatus, ReactNode> = {
            [ExportLogStatus.inProgress]: (
              <Button type="text" disabled>
                -
              </Button>
            ),
            [ExportLogStatus.succeed]: (
              <Button
                type="link"
                onClick={() => {
                  const urlr = url.replace('http://', 'https://');
                  download(urlr, filename)
                    .then((status) => {
                      if (status) {
                        message.success(t('export_modal_downloaded'));
                      }
                    })
                    .catch((res) => {
                      logger.error(
                        `[ExportCenterContent] exportDownloadError, ${formatLoggerData({
                          id,
                          url: urlr,
                          filename,
                          error: typeof res === 'string' ? res : res?.message || '',
                        })}`,
                      );
                      message.error(t('ViewShops.导出失败') + ':' + filename);
                    });
                }}
              >
                {t('export_modal_row_download')}
              </Button>
            ),
            [ExportLogStatus.failed]: (
              <Button
                type="link"
                onClick={() => retry(id)}
                disabled={retryIdRef.current === id && loading}
              >
                {t('export_modal_row_retry')}
              </Button>
            ),
          };
          return labelMap[status] || '-';
        },
      },
    ];
  }, [loading, retry, t]);
  return (
    <div className="w-full">
      <div>
        <div>{t('export_modal_tip1')}</div>
        <div>{t('export_modal_tip2')}</div>
      </div>
      <div className="w-full mt-4">
        <Table className="w-full" columns={columns} scroll={{ x: 'auto' }} {...tableProps} />
      </div>
    </div>
  );
};

export default ExportCenterContent;
